import logoUrl from "../assets/nebula-logo.png";
import logoDarkUrl from "../assets/nebula-logo-dark.png";
import markUrl from "../assets/nebula-mark.png";
import markDarkUrl from "../assets/nebula-mark-dark.png";
import ringUrl from "../assets/nebula-ring-gold.png";
import { useThemeChoice } from "../theme/theme";

// 品牌素材。全部取自業主 2023-11 的名片(`docs/source/nebula-namecard-2023-11.pdf`),
// 去背 PNG 收在 `src/assets/`。
//
// 這一支把「哪些素材承載資訊、哪些只是裝飾」一次分清楚,別處不要再自己寫 <img>:
//
//   BrandMark  星座 + 手寫字標(或只有星座)。它回答「這是誰的系統」,
//              所以有替代文字、不 aria-hidden —— 看不到畫面的人一樣要知道。
//   BrandRing  金色筆刷圈。純裝飾,一律 aria-hidden + alt=""。
//              螢幕閱讀器唸出「金色筆刷圈」對登入這件事沒有任何幫助。
//
// 金黃 #EBC12F 在白底上只有約 1.7:1。筆刷圈是圖片不是文字,不受那條限制;
// 但同一個金黃拿去寫字就是看得到讀不清 —— 要金色的文字一律用 --brand-gold-deep。
//
// ── 素材的一處落差,記在這裡免得下一個人以為是手滑 ────────────────────
//
// `assets/brand/nebula-ring-gold.png` 抽出來的其實是**整個名片正面**去背的結果:
// 圈裡還留著一份星座 + 字標,右緣還帶著一條名片裁切線(RGB 96,97,97,不透明)。
// 直接拿來當背景,登入頁上會是「一條灰槓 + 兩份字標疊在一起」。
//
// 所以 `src/assets/nebula-ring-gold.png` 是從那張圖**只留暖黃筆刷**再裁邊
// 得到的,不是逐位元組的複本(判準:r>120 且 r-b>45 且 g-b>20 ——
// 灰條 R-B≈-1、星座是藍紫 B>R、字標近黑 R 低,三者都會被刷掉)。
// 另外三個素材則與 `assets/brand/` 完全相同。
// 名片素材若之後重抽,請一併把「圈」單獨抽乾淨,這段就可以拿掉。
//
// ── 為什麼會有 -dark 那兩份 ────────────────────────────────────────
//
// 名片是印在白紙上的,所以 LOGO 是「近黑墨水 + 去背」:手寫字標有 95% 是純黑,
// 壓在深色模式的 --surface #1A1B28 上只有 1.23:1,整張圖 99.7% 的不透明像素
// 達不到 3:1 —— 深色 OS 的使用者看到的是一塊空白。
// LOGO 是 logotype,不受 WCAG 1.4.3 約束,但看不見的 LOGO 等於沒有 LOGO。
//
// 所以 `nebula-logo-dark.png` / `nebula-mark-dark.png` 是從對應的淺色版
// **只把 HLS 的亮度翻面**(L' = 1 - L,色相與彩度原封不動)得到的:
// 近黑的字標翻成近白,藍紫的星座翻成淺藍紫 —— 品牌的顏色還在,只是亮起來。
// 翻完之後在深色 --surface 上有 99.9% 的不透明像素達到 3:1。
// tests/brand.test.tsx 會把 PNG 解出來逐個像素算給你看,改素材前先看那一支。

/** logo:星座 + 字標(512×228)。mark:只有星座(512×143),小尺寸與 favicon 用。 */
export type BrandMarkVariant = "logo" | "mark";

const SOURCES: Record<BrandMarkVariant, string> = {
  logo: logoUrl,
  mark: markUrl,
};

/** 深色底上用的反白版。素材的由來見上方說明。 */
const DARK_SOURCES: Record<BrandMarkVariant, string> = {
  logo: logoDarkUrl,
  mark: markDarkUrl,
};

/**
 * LOGO 的替代文字,單一來源。
 *
 * 中英並列:中文是公司登記名,英文是字標上真的印著的那一個。
 * 兩邊都留著,不管使用者記得的是哪一個名字都找得到。
 */
export const BRAND_ALT = "星采設計 Nebula";

export interface BrandMarkProps {
  variant?: BrandMarkVariant;
  className?: string;
}

export function BrandMark({ variant = "logo", className = "" }: BrandMarkProps) {
  // 手動選了主題(業主 2026-09-25)就照它挑檔案;沒選過才交給系統設定。
  const choice = useThemeChoice();
  if (choice) {
    return (
      <img
        src={choice === "dark" ? DARK_SOURCES[variant] : SOURCES[variant]}
        alt={BRAND_ALT}
        draggable={false}
        className={`select-none ${className}`.trim()}
      />
    );
  }
  return (
    // <picture> 只負責挑檔案,語意仍然全部掛在 <img> 上 ——
    // 替代文字只寫一次,深淺兩份素材是同一個 LOGO,不是兩個東西。
    //
    // 外層給 display:contents(Tailwind 的 `contents`):讓 <img> 仍然是版面上
    // 那個直接的子元素。少了這一行,登入頁那個把 LOGO 疊在筆刷圈上的
    // flex 置中會多出一層盒子,圈與字標就對不準了。
    <picture className="contents">
      <source media="(prefers-color-scheme: dark)" srcSet={DARK_SOURCES[variant]} />
      <img
        src={SOURCES[variant]}
        alt={BRAND_ALT}
        // 拖曳一個 LOGO 到別的視窗不是任何人的意圖,只會把它從版面上拉歪。
        draggable={false}
        className={`select-none ${className}`.trim()}
      />
    </picture>
  );
}

export interface BrandRingProps {
  className?: string;
}

/** 金色筆刷圈。只當背景裝飾用,不接點擊、不進無障礙樹。 */
export function BrandRing({ className = "" }: BrandRingProps) {
  return (
    <img
      src={ringUrl}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`pointer-events-none select-none ${className}`.trim()}
    />
  );
}
